import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import * as React from "react";
import { createContext, useContext } from "react";

interface AccordionItemProps {
	children?: React.ReactNode;
}

const AccordionContext = createContext<{
	variant: "default" | "compact";
}>({
	variant: "default",
});

const Accordion = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
		variant?: "default" | "compact";
	}
>(({ className, variant = "default", ...props }, ref) => (
	<AccordionContext.Provider value={{ variant }}>
		<AccordionPrimitive.Root
			ref={ref}
			className={clsx(
				"flex flex-col w-full rounded-md overflow-hidden",
				variant === "default" && "gap-3",
				variant === "compact" && "border border-border/50 gap-0",
				className,
			)}
			{...props}
		/>
	</AccordionContext.Provider>
));
Accordion.displayName = AccordionPrimitive.Root.displayName;

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, children, ...props }, ref) => {
	const { variant } = useContext(AccordionContext);
	return (
		<AccordionPrimitive.Item
			ref={ref}
			className={clsx(
				"flex flex-col w-full bg-background overflow-hidden",
				variant === "default" && "rounded-md border border-border/50",
				variant === "compact" && "border-b last:border-b-0 border-border/50",
				className,
			)}
			{...props}
		>
			{children}
		</AccordionPrimitive.Item>
	);
});
AccordionItem.displayName = AccordionPrimitive.Item.displayName;

const AccordionTrigger = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Header className="flex">
		<AccordionPrimitive.Trigger
			ref={ref}
			className={clsx(
				"group flex flex-row items-center justify-between w-full gap-2 text-left transition hover:bg-slate-50",
				" data-[state=open]:border-b border-border/50",
				className,
			)}
			{...props}
		>
			{children}
			<span
				className={clsx(
					"ml-2 text-foreground transition-transform opacity-50 group-hover:opacity-100",
					"group-data-[state=open]:rotate-180 group-data-[state=open]:opacity-100",
				)}
			>
				<IconChevronDown size={18} stroke={2} />
			</span>
		</AccordionPrimitive.Trigger>
	</AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className={clsx(
			"data-[state=open]:animate-accordionSlideDown data-[state=closed]:animate-accordionSlideUp",
			className,
		)}
		{...props}
	>
		<div className="flex flex-col w-full">{children}</div>
	</AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
